import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Settings,
  Bell,
  Download,
  RefreshCw,
  Send,
  ChevronDown,
  Check,
  X,
  Clock,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import DocumentViewer from "./DocumentViewer";
import DynamicContentRenderer from "./DynamicContentRenderer";
import AuditLogModal from "./AuditLogModal";
import { regenerateInvoiceProcessing, updateInvoiceProcessing, getInvoiceProcessing } from "../../services/playgroundService";
import { executeAgent } from "../../utils/Product";
import { generateSessionId } from "../../utils/session";
import {
  buildRunAgentRegenerateFourayResponse,
  expandOrchestratorResponseForPlayground,
  formatDisplayValue,
  normalizeFourayResponseForPlayground,
  normalizeSectionTablesForPlayground,
} from "../../utils/runAgentResponse";
import axiosInstance from "../../utils/api";
import "./Playground.css";

const Playground = ({
  documentUrl,
  documentType,
  documentName,
  apiEndpoint,
  initialContent = null,
  backLink,
  backLinkText = "Back to Invoice Workbench",
  topActions = [],
  hideDocumentViewer = false,
  resourceType = "invoice_processings",
}) => {
  const navigate = useNavigate();
  const [contentData, setContentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exceptionsDropdownOpen, setExceptionsDropdownOpen] = useState(false);
  const [fieldValues, setFieldValues] = useState({});
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingFieldId, setSavingFieldId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [exceptionStatus, setExceptionStatus] = useState(initialContent?.exception_status || null);
  const [isDocumentPanelCollapsed, setIsDocumentPanelCollapsed] = useState(false);
  const hasPendingChangesRef = useRef(false);
  const saveInFlightRef = useRef(false);
  const dropdownRef = useRef(null);

  // Helper function to convert key to label (e.g., "invoice_number" -> "Invoice Number")
  const keyToLabel = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to detect field type based on key and value
  const detectFieldType = (key, value) => {
    const lowerKey = key.toLowerCase();
    
    // Date fields are treated as text fields (not HTML5 date inputs)
    // if (lowerKey.includes('date')) return 'date';
    if (lowerKey.includes('email')) return 'email';
    if (lowerKey.includes('phone') || lowerKey.includes('mobile')) return 'tel';
    if (lowerKey.includes('address') || lowerKey.includes('notes') || lowerKey.includes('description')) {
      return typeof value === 'string' && value.length > 50 ? 'textarea' : 'text';
    }
    if (lowerKey.includes('price') || lowerKey.includes('amount') || lowerKey.includes('total') || lowerKey.includes('subtotal') || lowerKey.includes('tax')) {
      return 'text'; 
    }
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'checkbox';
    
    return 'text';
  };

  // Recursively transform object to fields
  const transformObjectToFields = (obj, prefix = '', sectionTitle = '') => {
    const fields = [];
    
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      const fieldId = prefix ? `${prefix}_${key}` : key;
      
      if (value === null || value === undefined || value === '') {
        fields.push({
          id: fieldId,
          label: keyToLabel(key),
          type: detectFieldType(key, ''),
          value: '',
          editable: true,
          required: false,
        });
      } else if (Array.isArray(value)) {
        if (value.length > 0 && typeof value[0] === 'object') {
          // Arrays of objects are rendered as tables at the response level
          return;
        }
        fields.push({
          id: fieldId,
          label: keyToLabel(key),
          type: 'text',
          value: formatDisplayValue(value),
          editable: true,
          required: false,
        });
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        const nestedFields = transformObjectToFields(value, fieldId, keyToLabel(key));
        if (nestedFields && nestedFields.length > 0) {
          fields.push(...nestedFields);
        }
      } else {
        const fieldType = detectFieldType(key, value);
        fields.push({
          id: fieldId,
          label: keyToLabel(key),
          type: fieldType,
          value: formatDisplayValue(value),
          editable: true,
          required: false,
        });
      }
    });
    
    return fields;
  };

  const sanitizeTableRows = (rows) =>
    (rows || []).map((row) => {
      if (!row || typeof row !== "object") return row;
      return Object.fromEntries(
        Object.entries(row).map(([key, val]) => [
          key,
          val != null && typeof val === "object" ? formatDisplayValue(val) : val,
        ])
      );
    });

  // Preserve column order as it first appears in agent rows (no hardcoded keys).
  const getColumnOrderFromRows = (rows) => {
    const orderedKeys = [];
    const seen = new Set();

    (rows || []).forEach((row) => {
      if (!row || typeof row !== "object" || Array.isArray(row)) return;
      Object.keys(row).forEach((key) => {
        if (!seen.has(key)) {
          seen.add(key);
          orderedKeys.push(key);
        }
      });
    });

    return orderedKeys;
  };

  const getOrderedColumnsForTable = (tableKey, rows, tableMeta = {}) => {
    const dataKeys = getColumnOrderFromRows(rows);
    const metaValue = tableMeta?.[tableKey];
    const apiOrder = Array.isArray(metaValue)
      ? metaValue
      : Array.isArray(metaValue?.column_order)
      ? metaValue.column_order
      : [];

    if (!apiOrder.length) {
      return dataKeys.map((colKey) => ({
        accessorKey: colKey,
        header: keyToLabel(colKey),
      }));
    }

    const seen = new Set();
    const orderedKeys = [];

    apiOrder.forEach((key) => {
      if (dataKeys.includes(key) && !seen.has(key)) {
        seen.add(key);
        orderedKeys.push(key);
      }
    });

    dataKeys.forEach((key) => {
      if (!seen.has(key)) {
        seen.add(key);
        orderedKeys.push(key);
      }
    });

    return orderedKeys.map((colKey) => ({
      accessorKey: colKey,
      header: keyToLabel(colKey),
    }));
  };

  // Transform API response dynamically
  const transformApiResponse = (apiData) => {
    const sections = [];
    const normalized = normalizeFourayResponseForPlayground(apiData);
    
    // Extract the main response data from fouray_response.response or response
    let responseData = normalized.fouray_response?.response || normalized.response || normalized;
    if (responseData && typeof responseData === "object" && !Array.isArray(responseData)) {
      responseData = expandOrchestratorResponseForPlayground(responseData);
      responseData = normalizeSectionTablesForPlayground(responseData);
    }

    const tableMeta =
      responseData?.__table_meta &&
      typeof responseData.__table_meta === "object" &&
      !Array.isArray(responseData.__table_meta)
        ? responseData.__table_meta
        : {};

    // Handle exception field - create exceptions section if exception exists
    // Check if exception is an array (saved exceptions) or string (first time) in fouray_response.response.exception
    let exceptionsArray = [];
    
    // Check for exception in fouray_response.response.exception
    const exceptionData = responseData.exception;
    
    if (exceptionData && Array.isArray(exceptionData)) {
      // Use saved exceptions array from exception (already in array format)
      exceptionsArray = exceptionData.map((exc, index) => ({
        id: `exception-${index + 1}`,
        type: "error",
        message: formatDisplayValue(typeof exc === "object" ? exc.message ?? exc : exc),
        isResolved: exc.isResolved || false,
        status: exc.isResolved ? "resolved" : "pending",
        actions: [
          {
            label: exc.isResolved ? "Mark as unresolved" : "Mark as resolved",
            action: "resolve",
            type: "primary"
          }
        ]
      }));
    } else if (exceptionData && typeof exceptionData === 'string' && exceptionData.trim()) {
      // First time - parse exception string and split by ;
      const exceptionStrings = exceptionData.split(';').map(s => s.trim()).filter(s => s.length > 0);
      exceptionsArray = exceptionStrings.map((exc, index) => ({
        id: `exception-${index + 1}`,
        type: "error",
        message: exc,
        isResolved: false, // Default to false for first time
        status: "pending",
        actions: [
          {
            label: "Mark as resolved",
            action: "resolve",
            type: "primary"
          }
        ]
      }));
    }
    
    if (exceptionsArray.length > 0) {
      const exceptionsSection = {
        id: "exceptions",
        title: "Exceptions",
        type: "exceptions",
        exceptions: exceptionsArray
      };
      sections.push(exceptionsSection);
    }
    
    // Separate top-level fields, nested objects, and arrays
    const topLevelFields = [];
    const nestedObjects = {};
    const arrays = {};
    
    Object.keys(responseData).forEach((key) => {
      // Skip exception field as it's already handled above
      if (key === 'exception' || key === '__table_meta') return;
      
      const value = responseData[key];
      
      if (Array.isArray(value)) {
        arrays[key] = value;
      } else if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
        nestedObjects[key] = value;
      } else {
        // Top-level primitive
        topLevelFields.push({
          key,
          value,
        });
      }
    });
    
    // Add top-level fields directly (without wrapping in a section)
    if (topLevelFields.length > 0) {
      const fields = topLevelFields.map(({ key, value }) => {
        const fieldType = detectFieldType(key, value);
        const displayValue = formatDisplayValue(value);
        
        return {
          id: key,
          label: keyToLabel(key),
          type: fieldType,
          value: displayValue,
          editable: true,
          required: false,
        };
      });
      
      // Add fields as an object with fields array (no section wrapper)
      sections.push({
        fields,
      });
    }
    
    // Create sections for nested objects only if they have multiple fields
    // Otherwise, add fields directly
    Object.keys(nestedObjects).forEach((key) => {
      const nestedObj = nestedObjects[key];

      // Unexpanded project node — flatten and merge sections
      if (nestedObj && typeof nestedObj === "object" && Array.isArray(nestedObj.files)) {
        const expanded = expandOrchestratorResponseForPlayground({ [key]: nestedObj });
        const nestedSections = transformApiResponse({ fouray_response: { response: expanded } });
        nestedSections.forEach((section) => {
          if (section.type !== "exceptions") sections.push(section);
        });
        return;
      }

      const fields = transformObjectToFields(nestedObj, key);
      
      if (fields && fields.length > 0) {
        // Only create a section if there are multiple fields or if it makes sense to group them
        // For now, always create sections for nested objects to keep them organized
        sections.push({
          id: `${key}-details`,
          title: keyToLabel(key),
          type: 'form',
          fields,
        });
      }
    });
    
    // Create table sections for arrays
    Object.keys(arrays).forEach((key) => {
      const arrayData = arrays[key];
      
      if (arrayData.length > 0 && typeof arrayData[0] === 'object') {
        const columns = getOrderedColumnsForTable(key, arrayData, tableMeta);
        
        sections.push({
          id: key,
          title: keyToLabel(key),
          type: 'table',
          table: {
            data: sanitizeTableRows(arrayData),
            columns,
            onSearch: null,
            onDelete: null,
            onDuplicate: null,
            gridView: false,
          },
        });
      } else if (arrayData.length > 0) {
        sections.push({
          id: `${key}-list`,
          fields: [{
            id: key,
            label: keyToLabel(key),
            type: 'text',
            value: formatDisplayValue(arrayData),
            editable: true,
            required: false,
          }],
        });
      }
    });
    
    // Reorder sections: if both line_items and amounts exist, put amounts right after line_items
    const lineItemsIndex = sections.findIndex(s => s.id === 'line_items');
    const amountsIndex = sections.findIndex(s => s.id === 'amounts-details');
    
    if (lineItemsIndex !== -1 && amountsIndex !== -1 && amountsIndex !== lineItemsIndex + 1) {
      // Remove amounts section from its current position
      const [amountsSection] = sections.splice(amountsIndex, 1);
      // Find new line_items index (may have shifted after splice)
      const newLineItemsIndex = sections.findIndex(s => s.id === 'line_items');
      // Insert amounts right after line_items
      sections.splice(newLineItemsIndex + 1, 0, amountsSection);
    }
    
    return sections;
  };

  const getMockData = () => {
    // Use the dummy invoice object
    const dummyInvoiceData = {
      response: {
        invoice_number: "G-INV-001",
        invoice_date: "2025-01-01",
        due_date: "",
        currency: "USD",
        vendor: {
          name: "Google Cloud",
          address: "",
          phone: "",
          email: "",
          gstin: ""
        },
        customer: {
          name: "Test User",
          address: "",
          phone: "",
          email: "",
          gstin: ""
        },
        line_items: [
          {
            description: "Compute Engine",
            quantity: "1",
            unit_price: "120.00",
            total: "120.00"
          },
          {
            description: "Cloud Storage",
            quantity: "1",
            unit_price: "45.00",
            total: "45.00"
          },
          {
            description: "BigQuery",
            quantity: "1",
            unit_price: "90.00",
            total: "90.00"
          }
        ],
        amounts: {
          subtotal: "255.00",
          tax: "0.00",
          total: "255.00"
        },
        payment_terms: "",
        notes: "Parsed from document titled 'Google Cloud Invoice' (page1). Source contains invoice number, date, customer name, service line items and total. Missing fields: vendor/customer addresses, contact details, GSTIN, due date, explicit quantities and tax breakdown. Quantities assumed as1 where unspecified."
      },
      agent_id: 79,
      session_id: null
    };
    
    // Transform the dummy data using the dynamic transformation function
    const sections = transformApiResponse(dummyInvoiceData);
    
    // Add exceptions section at the beginning
    const exceptionsSection = {
      id: "exceptions",
      title: "Exceptions",
      type: "exceptions",
      exceptions: [
        {
          id: "exception-1",
          type: "error",
          message: "Vendor code not found.",
          status: "pending",
          actions: [
            {
              label: "Marked as resolved",
              action: "resolve",
              type: "primary"
            }
          ]
        },
        {
          id: "exception-2",
          type: "error",
          message: "Review line item descriptions for categorization.",
          status: "pending",
          actions: [
            {
              label: "Resolve",
              action: "resolve",
              type: "secondary"
            }
          ]
        }
      ]
    };
    
    return [exceptionsSection, ...sections];
  };

  // Fetch content data from API
  useEffect(() => {
    if (initialContent) {
      let sections = [];
      
      try {
        if (initialContent.sections) {
          sections = initialContent.sections;
        } else if (
          initialContent.fouray_response ||
          initialContent.response ||
          initialContent.invoice_number ||
          initialContent.vendor
        ) {
          sections = transformApiResponse(initialContent);
        } else if (Array.isArray(initialContent)) {
          sections = initialContent;
        }
      } catch (transformErr) {
        console.error("Failed to transform playground content:", transformErr);
        setError("Failed to display invoice data for this record.");
        sections = [];
      }
      
      setContentData(sections);
      const initialValues = {};
      sections.forEach((section) => {
        if (section.fields) {
          section.fields.forEach((field) => {
            if (field.value) {
              initialValues[field.id] = field.value;
            }
          });
        }
      });
      setFieldValues(initialValues);

      // Capture session id from API payload (used for regenerate)
      const sId =
        initialContent?.fouray_response?.session_id ||
        initialContent?.session_id ||
        initialContent?.fouray_response?.sessionId ||
        null;
      setSessionId(sId);
      
      // Set exception status from initial content
      if (initialContent?.exception_status) {
        setExceptionStatus(initialContent.exception_status);
      }
    } else if (apiEndpoint) {
      fetchContentData();
    } else {
      // Use mock data if no API endpoint provided
      const mockData = getMockData();
      setContentData(mockData);
      const initialValues = {};
      mockData.forEach((section) => {
        if (section.fields) {
          section.fields.forEach((field) => {
            if (field.value) {
              initialValues[field.id] = field.value;
            }
          });
        }
      });
      setFieldValues(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiEndpoint, initialContent]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setExceptionsDropdownOpen(false);
      }
    };

    if (exceptionsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [exceptionsDropdownOpen]);

  const fetchContentData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(apiEndpoint);
      
      // Check if response is actually JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        // If not JSON, use mock data
        const mockData = getMockData();
        setContentData(mockData);
        const initialValues = {};
        mockData.forEach((section) => {
          if (section.fields) {
            section.fields.forEach((field) => {
              if (field.value) {
                initialValues[field.id] = field.value;
              }
            });
          }
        });
        setFieldValues(initialValues);
        setLoading(false);
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Transform API response to match our structure
      let sections = [];
      
      if (data.sections) {
        // If API already returns sections in our format
        sections = data.sections;
      } else if (data.response || data.invoice_number || data.vendor) {
        // Transform invoice response format
        sections = transformApiResponse(data);
      } else if (Array.isArray(data)) {
        sections = data;
      } else {
        sections = [data];
      }
      
      setContentData(sections);
      
      // Initialize field values from API data
      const initialValues = {};
      sections.forEach((section) => {
        if (section.fields) {
          section.fields.forEach((field) => {
            if (field.value) {
              initialValues[field.id] = field.value;
            }
          });
        }
      });
      setFieldValues(initialValues);
    } catch (err) {
      console.error("Error fetching content data:", err);
      // Use mock data on error
      const mockData = getMockData();
      setContentData(mockData);
      const initialValues = {};
      mockData.forEach((section) => {
        if (section.fields) {
          section.fields.forEach((field) => {
            if (field.value) {
              initialValues[field.id] = field.value;
            }
          });
        }
      });
      setFieldValues(initialValues);
      // Don't show error if we have mock data to display
      // setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper to parse currency/number values
  const parseAmount = (value) => {
    if (!value) return 0;
    // Remove currency symbols, commas, and parse as float
    const cleaned = String(value).replace(/[^0-9.-]/g, '');
    return parseFloat(cleaned) || 0;
  };

  // Helper to format amount (preserves original format style)
  const formatAmount = (value, originalValue) => {
    // Check if original had currency symbol
    const hasCurrency = originalValue && /^[^\d]/.test(String(originalValue).trim());
    const currencyMatch = String(originalValue || '').match(/^([^\d\s]+)/);
    const currency = currencyMatch ? currencyMatch[1] : '';
    
    // Format number
    const numValue = Number(value);
    if (isNaN(numValue)) return originalValue || '';
    
    let formatted;
    
    // Check if it's a whole number (no decimal part)
    if (numValue % 1 === 0) {
      // Remove .00 for whole numbers
      formatted = numValue.toString();
    } else {
      // For decimal numbers, preserve precision up to 2 decimal places
      // Don't force trailing zeros - "0.5" stays "0.5", not "0.50"
      const valueStr = numValue.toString();
      const decimalPart = valueStr.split('.')[1];
      
      if (decimalPart && decimalPart.length <= 2) {
        // Preserve the exact decimal places (1 or 2)
        formatted = valueStr;
      } else if (decimalPart && decimalPart.length > 2) {
        // Round to 2 decimal places if more than 2
        formatted = numValue.toFixed(2);
      } else {
        // Fallback
        formatted = numValue.toFixed(2);
      }
    }
    
    return hasCurrency ? `${currency}${formatted}` : formatted;
  };

  const handleFieldChange = (fieldId, value) => {
    hasPendingChangesRef.current = true;
    setFieldValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));

    // Update contentData to reflect the change
    setContentData((prev) => {
      let updatedSections = prev.map((section) => {
        if (section.fields) {
          return {
            ...section,
            fields: section.fields.map((field) =>
              field.id === fieldId ? { ...field, value } : field
            ),
          };
        }
        return section;
      });

      // If tax changed, recalculate total tax percentage and total
      if (fieldId === 'amounts_tax') {
        const amountsSection = updatedSections.find(s => s.id === 'amounts-details');
        if (amountsSection?.fields) {
          // Use latest values from fieldValues, fallback to field.value
          const currentSubtotal = fieldValues['amounts_subtotal'] || 
            amountsSection.fields.find(f => f.id === 'amounts_subtotal')?.value || '0';
          const subtotal = parseAmount(currentSubtotal);
          
          // Get original format references
          let originalTotal = '';
          let originalTaxPercentage = '';
          amountsSection.fields.forEach(field => {
            if (field.id === 'amounts_total') {
              originalTotal = field.value;
            }
            if (field.id === 'amounts_total_tax_percentage') {
              originalTaxPercentage = field.value || '0';
            }
          });

          const newTax = parseAmount(value);
          
          // Calculate new tax percentage: (tax amount / subtotal) * 100
          let newTaxPercentage = 0;
          if (subtotal > 0 && newTax > 0) {
            newTaxPercentage = (newTax / subtotal) * 100;
          }
          
          const formattedTaxPercentage = formatAmount(newTaxPercentage, originalTaxPercentage);
          const newTotal = subtotal + newTax;
          const formattedTotal = formatAmount(newTotal, originalTotal);

          updatedSections = updatedSections.map((section) => {
            if (section.id === 'amounts-details' && section.fields) {
              return {
                ...section,
                fields: section.fields.map((field) => {
                  if (field.id === 'amounts_total') {
                    return { ...field, value: formattedTotal };
                  }
                  if (field.id === 'amounts_total_tax_percentage') {
                    return { ...field, value: formattedTaxPercentage };
                  }
                  return field;
                }),
              };
            }
            return section;
          });

          // Also update fieldValues for total and tax percentage
          setFieldValues(prevValues => ({
            ...prevValues,
            amounts_total: formattedTotal,
            amounts_total_tax_percentage: formattedTaxPercentage,
          }));
        }
      }

      // If subtotal changed, recalculate tax amount from tax percentage, then total
      if (fieldId === 'amounts_subtotal') {
        const amountsSection = updatedSections.find(s => s.id === 'amounts-details');
        if (amountsSection?.fields) {
          // Get tax percentage (source of truth)
          const currentTaxPercentage = fieldValues['amounts_total_tax_percentage'] || 
            amountsSection.fields.find(f => f.id === 'amounts_total_tax_percentage')?.value || '0';
          const taxPercentage = parseAmount(currentTaxPercentage);
          
          // Get original format references
          let originalTotal = '';
          let originalTax = '';
          amountsSection.fields.forEach(field => {
            if (field.id === 'amounts_total') {
              originalTotal = field.value;
            }
            if (field.id === 'amounts_tax') {
              originalTax = field.value;
            }
          });

          const newSubtotal = parseAmount(value);
          // Calculate tax amount from subtotal and tax percentage: tax = subtotal × (percentage / 100)
          const newTax = (newSubtotal * taxPercentage) / 100;
          const formattedTax = formatAmount(newTax, originalTax);
          const newTotal = newSubtotal + newTax;
          const formattedTotal = formatAmount(newTotal, originalTotal);

          updatedSections = updatedSections.map((section) => {
            if (section.id === 'amounts-details' && section.fields) {
              return {
                ...section,
                fields: section.fields.map((field) => {
                  if (field.id === 'amounts_tax') {
                    return { ...field, value: formattedTax };
                  }
                  if (field.id === 'amounts_total') {
                    return { ...field, value: formattedTotal };
                  }
                  return field;
                }),
              };
            }
            return section;
          });

          // Also update fieldValues for tax and total
          setFieldValues(prevValues => ({
            ...prevValues,
            amounts_tax: formattedTax,
            amounts_total: formattedTotal,
          }));
        }
      }

      // If tax percentage changed, recalculate tax amount from subtotal and percentage, then total
      if (fieldId === 'amounts_total_tax_percentage') {
        const amountsSection = updatedSections.find(s => s.id === 'amounts-details');
        if (amountsSection?.fields) {
          // Get current subtotal
          const currentSubtotal = fieldValues['amounts_subtotal'] || 
            amountsSection.fields.find(f => f.id === 'amounts_subtotal')?.value || '0';
          const subtotal = parseAmount(currentSubtotal);
          
          // Get original format references
          let originalTotal = '';
          let originalTax = '';
          amountsSection.fields.forEach(field => {
            if (field.id === 'amounts_total') {
              originalTotal = field.value;
            }
            if (field.id === 'amounts_tax') {
              originalTax = field.value;
            }
          });

          const newTaxPercentage = parseAmount(value);
          // Calculate tax amount from subtotal and tax percentage: tax = subtotal × (percentage / 100)
          const newTax = (subtotal * newTaxPercentage) / 100;
          const formattedTax = formatAmount(newTax, originalTax);
          const newTotal = subtotal + newTax;
          const formattedTotal = formatAmount(newTotal, originalTotal);

          updatedSections = updatedSections.map((section) => {
            if (section.id === 'amounts-details' && section.fields) {
              return {
                ...section,
                fields: section.fields.map((field) => {
                  if (field.id === 'amounts_tax') {
                    return { ...field, value: formattedTax };
                  }
                  if (field.id === 'amounts_total') {
                    return { ...field, value: formattedTotal };
                  }
                  return field;
                }),
              };
            }
            return section;
          });

          // Also update fieldValues for tax and total
          setFieldValues(prevValues => ({
            ...prevValues,
            amounts_tax: formattedTax,
            amounts_total: formattedTotal,
          }));
        }
      }

      return updatedSections;
    });
  };

  // Calculate line item total and update amounts section
  const calculateInvoiceAmounts = (lineItemsData, prevContentData, prevLineItemsData = null) => {
    // Find amounts section to get tax percentage (source of truth)
    const amountsSection = prevContentData.find(s => s.id === 'amounts-details');
    let taxPercentage = 0;
    let originalSubtotal = '';
    let originalTotal = '';
    let originalTaxValue = '';
    
    if (amountsSection?.fields) {
      amountsSection.fields.forEach(field => {
        if (field.id === 'amounts_total_tax_percentage') {
          taxPercentage = parseAmount(field.value);
        }
        if (field.id === 'amounts_tax') {
          originalTaxValue = field.value;
        }
        if (field.id === 'amounts_subtotal') {
          originalSubtotal = field.value;
        }
        if (field.id === 'amounts_total') {
          originalTotal = field.value;
        }
      });
    }

    // Calculate new subtotal from line items
    let newSubtotal = 0;
    const updatedLineItems = lineItemsData.map((item, index) => {
      const prevItem = prevLineItemsData && prevLineItemsData[index] ? prevLineItemsData[index] : null;
      
      const quantity = parseAmount(item.quantity) || 1; // Default to 1 if empty
      const unitPrice = parseAmount(item.unit_price);
      const enteredTotal = parseAmount(item.total);
      
      // Calculate what total should be from unit_price * quantity
      const calculatedTotal = quantity * unitPrice;
      
      // Get previous values for comparison
      const prevUnitPrice = prevItem ? parseAmount(prevItem.unit_price) : null;
      const prevQuantity = prevItem ? (parseAmount(prevItem.quantity) || 1) : null;
      const prevTotal = prevItem ? parseAmount(prevItem.total) : null;
      
      // Check which field actually changed (with tolerance for floating point)
      const unitPriceChanged = prevItem && prevUnitPrice !== null && Math.abs(unitPrice - prevUnitPrice) > 0.01;
      const quantityChanged = prevItem && prevQuantity !== null && Math.abs(quantity - prevQuantity) > 0.01;
      const totalChanged = prevItem && prevTotal !== null && Math.abs(enteredTotal - prevTotal) > 0.01;
      
      let finalUnitPrice = unitPrice;
      let finalTotal = enteredTotal;
      
      // Priority 1: If unit_price changed, recalculate total (unit_price * quantity)
      if (unitPriceChanged && quantity > 0) {
        finalTotal = calculatedTotal;
        finalUnitPrice = unitPrice; // Keep the new unit_price
      }
      // Priority 2: If quantity changed, recalculate total (unit_price * quantity)
      else if (quantityChanged && unitPrice > 0) {
        finalTotal = calculatedTotal;
        finalUnitPrice = unitPrice; // Keep the current unit_price
      }
      // Priority 3: If total changed (and unit_price/quantity didn't change), calculate unit_price from total
      else if (totalChanged && !unitPriceChanged && !quantityChanged && quantity > 0) {
        finalUnitPrice = enteredTotal / quantity;
        finalTotal = enteredTotal; // Use the manually entered total
      }
      // Priority 4: If nothing changed but values don't match, use calculated total
      else if (!unitPriceChanged && !quantityChanged && !totalChanged) {
        // If current total doesn't match calculated, use calculated (synchronize)
        if (Math.abs(enteredTotal - calculatedTotal) > 0.01) {
          finalTotal = calculatedTotal;
        }
      }
      
      newSubtotal += finalTotal;
      
      // Helper to preserve user input format or format calculated value
      const preserveOrFormat = (calculatedValue, originalValue, wasUserInput) => {
        if (wasUserInput && originalValue) {
          // User entered this value - preserve exact format (e.g., "0.5" stays "0.5")
          const originalStr = String(originalValue).trim();
          // Check if the calculated value matches what user typed (allowing for parsing)
          const parsedOriginal = parseAmount(originalValue);
          if (Math.abs(parsedOriginal - calculatedValue) < 0.01) {
            // Values match - preserve user's exact input format
            return originalStr;
          }
        }
        // Value was calculated or doesn't match - format it
        return formatAmount(calculatedValue, originalValue || '');
      };
      
      // Determine which values were user input
      const unitPriceWasUserInput = unitPriceChanged;
      const totalWasUserInput = totalChanged && !unitPriceChanged && !quantityChanged;
      
      const formattedUnitPrice = preserveOrFormat(finalUnitPrice, item.unit_price, unitPriceWasUserInput);
      const formattedTotal = preserveOrFormat(finalTotal, item.total, totalWasUserInput);
      
      return {
        ...item,
        unit_price: formattedUnitPrice,
        total: formattedTotal
      };
    });

    // Calculate tax amount from subtotal and tax percentage: tax = subtotal × (percentage / 100)
    const newTax = (newSubtotal * taxPercentage) / 100;
    const formattedTax = formatAmount(newTax, originalTaxValue);
    
    // Calculate new total (subtotal + tax)
    const newTotal = newSubtotal + newTax;

    return {
      updatedLineItems,
      newSubtotal: formatAmount(newSubtotal, originalSubtotal),
      newTotal: formatAmount(newTotal, originalTotal),
      tax: formattedTax // Calculate tax from percentage
    };
  };

  const handleTableDataChange = (tableId, newData) => {
    hasPendingChangesRef.current = true;
    
    setContentData((prev) => {
      // Get previous line items data for comparison
      const prevLineItemsSection = prev.find(s => s.type === "table" && s.id === 'line_items');
      const prevLineItemsData = prevLineItemsSection?.table?.data || null;
      
      let updatedSections = prev.map((section) => {
        if (section.type === "table" && section.id === tableId && section.table) {
          // If this is line_items, calculate totals
          if (tableId === 'line_items') {
            const { updatedLineItems } = calculateInvoiceAmounts(newData, prev, prevLineItemsData);
            return {
              ...section,
              table: {
                ...section.table,
                data: updatedLineItems,
              },
            };
          }
          return {
            ...section,
            table: {
              ...section.table,
              data: newData,
            },
          };
        }
        return section;
      });

      // If line_items changed, also update amounts section
      if (tableId === 'line_items') {
        const { newSubtotal, newTotal, tax } = calculateInvoiceAmounts(newData, prev, prevLineItemsData);
        
        updatedSections = updatedSections.map((section) => {
          if (section.id === 'amounts-details' && section.fields) {
            return {
              ...section,
              fields: section.fields.map((field) => {
                if (field.id === 'amounts_subtotal') {
                  return { ...field, value: newSubtotal };
                }
                if (field.id === 'amounts_tax') {
                  return { ...field, value: tax };
                }
                if (field.id === 'amounts_total') {
                  return { ...field, value: newTotal };
                }
                return field;
              }),
            };
          }
          return section;
        });

        // Also update fieldValues for subtotal, tax, and total
        setFieldValues(prevValues => ({
          ...prevValues,
          amounts_subtotal: newSubtotal,
          amounts_tax: tax,
          amounts_total: newTotal,
        }));
      }

      // If tax table changed, recalculate total tax percentage
      if (tableId === 'taxes' || tableId === 'tax' || tableId?.includes('tax')) {
        // Get current subtotal first (needed for percentage calculations)
        const amountsSection = updatedSections.find(s => s.id === 'amounts-details');
        let subtotal = 0;
        let originalTaxPercentage = '';
        let originalTax = '';
        let originalTotal = '';
        
        if (amountsSection?.fields) {
          amountsSection.fields.forEach(field => {
            if (field.id === 'amounts_subtotal') {
              subtotal = parseAmount(fieldValues['amounts_subtotal'] || field.value || '0');
            }
            if (field.id === 'amounts_total_tax_percentage') {
              originalTaxPercentage = fieldValues['amounts_total_tax_percentage'] || field.value || '0';
            }
            if (field.id === 'amounts_tax') {
              originalTax = field.value || '0';
            }
            if (field.id === 'amounts_total') {
              originalTotal = field.value || '0';
            }
          });
        }

        // Sum all tax amounts from the tax table
        // Handle both percentage-based and amount-based tax rows
        let totalTaxAmount = 0;
        newData.forEach((row) => {
          // First, try to get tax amount directly
          let taxAmount = parseAmount(row.amount || row.tax_amount || row.value || row.total || '0');
          
          // If amount is 0 or very small, check if there's a percentage field
          if (taxAmount === 0 || taxAmount < 0.01) {
            // Try percentage fields
            const taxPercentage = parseAmount(row.percentage || row.tax_percentage || row.rate || row.percent || '0');
            if (taxPercentage > 0 && subtotal > 0) {
              // Convert percentage to amount: amount = subtotal × (percentage / 100)
              taxAmount = (subtotal * taxPercentage) / 100;
            }
          }
          
          totalTaxAmount += taxAmount;
        });

        // Calculate new tax percentage: (total tax amount / subtotal) * 100
        let newTaxPercentage = 0;
        if (subtotal > 0 && totalTaxAmount > 0) {
          newTaxPercentage = (totalTaxAmount / subtotal) * 100;
        }

        // Format the percentage (preserve original format if possible)
        const formattedTaxPercentage = formatAmount(newTaxPercentage, originalTaxPercentage);

        // Update amounts section with new tax amount and percentage
        updatedSections = updatedSections.map((section) => {
          if (section.id === 'amounts-details' && section.fields) {
            return {
              ...section,
              fields: section.fields.map((field) => {
                if (field.id === 'amounts_tax') {
                  const formattedTax = formatAmount(totalTaxAmount, originalTax);
                  return { ...field, value: formattedTax };
                }
                if (field.id === 'amounts_total_tax_percentage') {
                  return { ...field, value: formattedTaxPercentage };
                }
                if (field.id === 'amounts_total') {
                  // Recalculate total: subtotal + total tax
                  const newTotal = subtotal + totalTaxAmount;
                  const formattedTotal = formatAmount(newTotal, originalTotal);
                  return { ...field, value: formattedTotal };
                }
                return field;
              }),
            };
          }
          return section;
        });

        // Update fieldValues
        setFieldValues(prevValues => {
          const formattedTax = formatAmount(totalTaxAmount, originalTax);
          const newTotal = subtotal + totalTaxAmount;
          const formattedTotal = formatAmount(newTotal, originalTotal);
          
          return {
            ...prevValues,
            amounts_tax: formattedTax,
            amounts_total_tax_percentage: formattedTaxPercentage,
            amounts_total: formattedTotal,
          };
        });
      }

      return updatedSections;
    });
  };

  const handleExceptionStatusChange = async (status) => {
    const documentId = initialContent?.id;
    if (!documentId) {
      console.error("No document ID available for updating exception status");
      return;
    }

    try {
      setSaving(true);
      
      // Preserve existing fouray_response when updating exception_status
      // Get current data first to preserve fouray_response
      const currentData = initialContent?.fouray_response || null;
      
      // Send exception_status at top level, and preserve fouray_response if it exists
      const patchPayload = {
        exception_status: status
      };
      
      // Only include fouray_response if it exists to preserve it
      if (currentData) {
        patchPayload.fouray_response = currentData;
      }
      
      await axiosInstance.patch(`/api/v1/invoice_processings/${documentId}`, patchPayload);
      
      // Refetch the data to get the updated record with all data intact
      const response = await getInvoiceProcessing(documentId);
      const updatedData = response.data;
      
      // Update exception status state
      setExceptionStatus(status);
      
      // Update content data if we have fouray_response to preserve all data
      if (updatedData?.fouray_response || updatedData?.response) {
        const sections = transformApiResponse(updatedData);
        setContentData(sections);
        
        // Update field values
        const newValues = {};
        sections.forEach((section) => {
          if (section.fields) {
            section.fields.forEach((field) => {
              if (field.value) {
                newValues[field.id] = field.value;
              }
            });
          }
        });
        setFieldValues(newValues);
      } else if (updatedData?.fouray_response === null) {
        // If fouray_response is null after update, preserve existing data
        // Don't update contentData, just update the exception status
        console.warn('fouray_response is null after update, preserving existing data');
        // Keep existing contentData and fieldValues unchanged
      } else {
        // If fouray_response is null after update, log warning but keep existing data
        console.warn('fouray_response is null after update, preserving existing data');
      }
    } catch (err) {
      console.error("Error updating exception status:", err);
      setError(err.response?.data?.message || err.message || "Failed to update exception status");
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (actionType, targetId, actionData) => {
    switch (actionType) {
      case "resolve":
        // Update exception status locally first
        let updatedExceptions = null;
        setContentData((prev) =>
          prev.map((section) => {
            if (section.exceptions) {
              const newExceptions = section.exceptions.map((exception) => {
                if (exception.id === targetId) {
                  const newIsResolved = !exception.isResolved;
                  return {
                    ...exception,
                    isResolved: newIsResolved,
                    status: newIsResolved ? "resolved" : "pending",
                    actions: [
                      {
                        label: newIsResolved ? "Mark as unresolved" : "Mark as resolved",
                        action: "resolve",
                        type: "primary"
                      }
                    ]
                  };
                }
                return exception;
              });
              updatedExceptions = newExceptions;
              return {
                ...section,
                exceptions: newExceptions,
              };
            }
            return section;
          })
        );
        
        // Save exceptions array to API
        if (updatedExceptions) {
          const documentId = initialContent?.id;
          if (documentId) {
            try {
              setSaving(true);
              // Prepare exceptions array for saving (only message and isResolved)
              const exceptionsToSave = updatedExceptions.map(exc => ({
                message: exc.message,
                isResolved: exc.isResolved
              }));
              
              // Preserve existing fouray_response when updating exception
              const currentData = initialContent?.fouray_response || null;
              
              // Create updated fouray_response with exception array replacing the string in response.exception
              const updatedFourayResponse = currentData ? {
                ...currentData,
                response: {
                  ...(currentData.response || {}),
                  exception: exceptionsToSave  // Replace string with array
                }
              } : {
                response: {
                  exception: exceptionsToSave
                }
              };
              
              const patchPayload = {
                fouray_response: updatedFourayResponse
              };
              
              await axiosInstance.patch(`/api/v1/invoice_processings/${documentId}`, patchPayload);
              
              // Refetch the data to get the updated record
              const response = await getInvoiceProcessing(documentId);
              const updatedData = response.data;
              
              // Update exception status state (check if all are resolved)
              const allResolved = exceptionsToSave.every(exc => exc.isResolved);
              if (allResolved) {
                setExceptionStatus("resolved");
              } else {
                setExceptionStatus(null);
              }
              
              // Update content data if we have fouray_response
              if (updatedData?.fouray_response || updatedData?.response) {
                const sections = transformApiResponse(updatedData);
                setContentData(sections);
              }
            } catch (err) {
              console.error("Error updating exception:", err);
              setError(err.response?.data?.message || err.message || "Failed to update exception");
              // Revert the change on error
              setContentData((prev) =>
                prev.map((section) => {
                  if (section.exceptions) {
                    return {
                      ...section,
                      exceptions: section.exceptions.map((exception) =>
                        exception.id === targetId
                          ? { ...exception, isResolved: !exception.isResolved, status: exception.isResolved ? "pending" : "resolved" }
                          : exception
                      ),
                    };
                  }
                  return section;
                })
              );
            } finally {
              setSaving(false);
            }
          }
        }
        break;
      case "reject":
      case "hold":
      case "approve":
        // Handle other actions
        break;
      default:
        break;
    }
  };

  const handleRegenerate = async () => {
    // Get the document id from initialContent
    const documentId = initialContent?.id;
    
    if (!documentId) {
      console.error("No document ID available for regeneration");
      return;
    }

    setRegenerating(true);
    setLoading(true);
    setError(null);
    
    try {
      let data;

      // Run Agent flow (orchestrator / no upload): re-execute agent and update fouray_response
      if (hideDocumentViewer) {
        const agentId = initialContent?.agent_id;
        if (!agentId) {
          throw new Error("No agent ID available for regeneration");
        }

        const query =
          initialContent?.query ||
          "Extract invoice details and return JSON";
        const newSessionId = generateSessionId();

        let savedFourayResponse = null;

        const streamResult = await executeAgent(
          agentId,
          initialContent?.agent_name || null,
          newSessionId,
          query,
          null,
          {
            onComplete: async ({ finalResponse, rawFinalBuffer }) => {
              savedFourayResponse = buildRunAgentRegenerateFourayResponse(
                finalResponse,
                rawFinalBuffer
              );
              await updateInvoiceProcessing(documentId, savedFourayResponse);
            },
          }
        );

        if (!savedFourayResponse && streamResult?.finalResponse != null) {
          savedFourayResponse = buildRunAgentRegenerateFourayResponse(
            streamResult.finalResponse,
            streamResult.rawFinalBuffer
          );
          await updateInvoiceProcessing(documentId, savedFourayResponse);
        }

        setSessionId(newSessionId);
        const refreshed = await getInvoiceProcessing(documentId);
        const invoice = refreshed?.data?.data ?? refreshed?.data ?? initialContent;
        data = {
          ...invoice,
          fouray_response: savedFourayResponse ?? invoice?.fouray_response,
        };
      } else {
        // Upload flow: existing regenerate API (unchanged)
        const response = await regenerateInvoiceProcessing(documentId, sessionId);
        const apiResponse = response.data;
        data = apiResponse.regenerated_invoice || apiResponse.data || apiResponse;
        const newSessionId =
          data?.fouray_response?.session_id || data?.session_id || sessionId || null;
        setSessionId(newSessionId);
      }

      // Transform the new response and update content
      if (data?.fouray_response || data?.response || data?.invoice_number || data?.vendor) {
        const sections = transformApiResponse(data);
        setContentData(sections);
        
        // Update field values
        const newValues = {};
        sections.forEach((section) => {
          if (section.fields) {
            section.fields.forEach((field) => {
              if (field.value) {
                newValues[field.id] = field.value;
              }
            });
          }
        });
        setFieldValues(newValues);
      }
    } catch (err) {
      console.error("Error regenerating invoice processing:", err);
      setError(err.response?.data?.message || err.message || "Failed to regenerate");
    } finally {
      setRegenerating(false);
      setLoading(false);
    }
  };

  // Build the response object from current field values and content data
  // Optional tableOverride allows passing updated table data directly (avoids async state issues)
  const buildResponseFromFields = (tableOverride = null) => {
    const response = {};
    
    // Extract values from contentData sections
    contentData.forEach((section) => {
      if (section.fields) {
        // Determine the parent object key from section id (e.g., 'amounts-details' -> 'amounts')
        let parentKey = null;
        if (section.id && section.id.endsWith('-details')) {
          parentKey = section.id.replace('-details', '');
        }
        
        section.fields.forEach((field) => {
          const value = fieldValues[field.id] || field.value || '';
          
          if (parentKey) {
            // Field belongs to a nested object (e.g., amounts)
            // Remove the parent prefix from field id to get the actual key name
            const prefix = `${parentKey}_`;
            if (field.id.startsWith(prefix)) {
              const actualKey = field.id.substring(prefix.length);
              
              // Ensure parent object exists
              if (!response[parentKey] || typeof response[parentKey] !== 'object' || Array.isArray(response[parentKey])) {
                response[parentKey] = {};
              }
              
              // Set the value using the actual key (which may contain underscores)
              response[parentKey][actualKey] = value;
            } else {
              // Fallback: field id doesn't match expected pattern
              response[field.id] = value;
            }
          } else {
            // Top-level field - parse field id to reconstruct nested structure
            const parts = field.id.split('_');
            let current = response;
            
            for (let i = 0; i < parts.length - 1; i++) {
              // Check if the key exists and is an object, if not create/replace with object
              if (!current[parts[i]] || typeof current[parts[i]] !== 'object' || Array.isArray(current[parts[i]])) {
                current[parts[i]] = {};
              }
              current = current[parts[i]];
            }
            
            current[parts[parts.length - 1]] = value;
          }
        });
      }
      
      // Handle table data (line_items)
      if (section.type === 'table' && section.table?.data) {
        const tableKey = section.id || 'line_items';
        // Use tableOverride if provided for this table, otherwise use current state
        if (tableOverride && tableOverride.tableId === section.id) {
          response[tableKey] = tableOverride.data;
        } else {
          response[tableKey] = section.table.data;
        }
      }
    });
    
    return response;
  };

  const handleSave = async (tableOverride = null) => {
    const documentId = initialContent?.id;
    
    if (!documentId) {
      console.error("No document ID available for saving");
      return;
    }

    // Only save when something actually changed, and avoid duplicate in-flight saves
    if (!hasPendingChangesRef.current || saveInFlightRef.current) {
      return;
    }

    saveInFlightRef.current = true;
    setSaving(true);
    const startedAt = Date.now();
    
    try {
      // Build the response object from current field values
      // Pass tableOverride to use latest table data directly (avoids async state issues)
      const responseData = buildResponseFromFields(tableOverride);
      
      // Call update API
      await updateInvoiceProcessing(documentId, {
        response: responseData
      });
      hasPendingChangesRef.current = false;
    } catch (err) {
      console.error("Error saving invoice processing:", err);
      setError(err.response?.data?.message || err.message || "Failed to save");
    } finally {
      // Keep "Saving..." visible briefly so users can perceive it
      const elapsed = Date.now() - startedAt;
      const minVisibleMs = 500;
      if (elapsed < minVisibleMs) {
        await new Promise((resolve) => setTimeout(resolve, minVisibleMs - elapsed));
      }
      setSaving(false);
      saveInFlightRef.current = false;
    }
  };

  const handleFieldCommit = async (fieldId) => {
    // Save current edits on blur/enter
    setSavingFieldId(fieldId || null);
    await handleSave();
    setSavingFieldId(null);
  };

  const handleTableCommit = async (tableId, newData) => {
    // Ensure the latest table data is applied, then save
    if (newData) {
      handleTableDataChange(tableId, newData);
      // Pass the new data directly to handleSave to avoid async state timing issues
      // This ensures the save uses the correct data immediately
      await handleSave({ tableId, data: newData });
    } else {
      await handleSave();
    }
  };

  const handleTopAction = (action) => {
    if (action.handler) {
      action.handler();
    }
  };

  const handleDownload = async () => {
    const documentId = initialContent?.id;
    if (!documentId) {
      console.error("No document ID available for download");
      alert("Unable to download: Document ID not found");
      return;
    }

    try {
      // Build query parameters for columns
      const columns = ['payment_terms', 'line_items', 'invoice_url'];
      const queryParams = columns.map(col => `columns[]=${encodeURIComponent(col)}`).join('&');
      
      // Construct the download URL
      const downloadUrl = `/api/v1/invoice_processings/${documentId}/download_single?${queryParams}`;
      
      // Make the API call - get JSON response
      const response = await axiosInstance.get(downloadUrl);
      const responseData = response.data;

      // Convert JSON to CSV
      const csvContent = convertToCSV(responseData);
      
      // Create blob and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const filename = `invoice_${responseData.invoice_id || documentId}_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading invoice processing:", error);
      alert("Failed to download file. Please try again.");
    }
  };

  // Convert API response to CSV format
  const convertToCSV = (data) => {
    if (!data || !data.data || !data.data.rows) {
      return '';
    }

    const { columns, headers, rows } = data.data;
    
    // Get all unique keys from line_items objects to create column headers
    const lineItemKeys = new Set();
    const lineItemsIndex = columns.indexOf('line_items');
    if (lineItemsIndex >= 0) {
      rows.forEach(row => {
        const lineItems = row[lineItemsIndex];
        if (Array.isArray(lineItems)) {
          lineItems.forEach(item => {
            if (typeof item === 'object') {
              Object.keys(item).forEach(key => lineItemKeys.add(key));
            }
          });
        }
      });
    }
    
    const lineItemHeaders = Array.from(lineItemKeys);
    
    // Build CSV header row
    // Payment Terms, Invoice URL, then all line item fields
    const paymentTermsIndex = columns.indexOf('payment_terms');
    const invoiceUrlIndex = columns.indexOf('invoice_url');
    
    const csvHeaders = [];
    if (paymentTermsIndex >= 0) {
      csvHeaders.push(headers[paymentTermsIndex] || 'Payment Terms');
    }
    if (invoiceUrlIndex >= 0) {
      csvHeaders.push(headers[invoiceUrlIndex] || 'Invoice URL');
    }
    csvHeaders.push(...lineItemHeaders.map(key => key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')));
    
    // Escape CSV values
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      // If contains comma, quote, or newline, wrap in quotes and escape quotes
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };
    
    // Build CSV rows
    const csvRows = [];
    
    rows.forEach(row => {
      const paymentTerms = paymentTermsIndex >= 0 ? (row[paymentTermsIndex] || '') : '';
      const invoiceUrl = invoiceUrlIndex >= 0 ? (row[invoiceUrlIndex] || '') : '';
      const lineItems = lineItemsIndex >= 0 ? (row[lineItemsIndex] || []) : [];
      
      if (Array.isArray(lineItems) && lineItems.length > 0) {
        // Create one row per line item
        lineItems.forEach(item => {
          const csvRow = [];
          if (paymentTermsIndex >= 0) {
            csvRow.push(escapeCSV(paymentTerms));
          }
          if (invoiceUrlIndex >= 0) {
            csvRow.push(escapeCSV(invoiceUrl));
          }
          csvRow.push(...lineItemHeaders.map(key => escapeCSV(item[key] || '')));
          csvRows.push(csvRow.join(','));
        });
      } else {
        // No line items, just add the main row
        const csvRow = [];
        if (paymentTermsIndex >= 0) {
          csvRow.push(escapeCSV(paymentTerms));
        }
        if (invoiceUrlIndex >= 0) {
          csvRow.push(escapeCSV(invoiceUrl));
        }
        csvRow.push(...lineItemHeaders.map(() => ''));
        csvRows.push(csvRow.join(','));
      }
    });
    
    // Combine headers and rows
    return [csvHeaders.join(','), ...csvRows].join('\n');
  };

  const defaultTopActions = [
    { label: "Audit Log", icon: Clock, handler: () => setAuditLogOpen(true) },
    { label: regenerating ? "Regenerating..." : "Regenerate", icon: RefreshCw, handler: handleRegenerate, disabled: regenerating, isLoading: regenerating },
    {
      label: "Status",
      icon: ChevronDown,
      hasDropdown: true,
      dropdownOptions: [
        { label: "Approve", value: "approve", icon: Check },
        { label: "Reject", value: "reject", icon: X },
        { label: "Hold", value: "hold", icon: Clock },
      ],
    },
    { label: "Download", icon: Download, handler: handleDownload },
  ];

  const hasDocumentForDownload = Boolean(documentUrl);
  const actionsBase = topActions.length > 0 ? topActions : defaultTopActions;
  const actionsToUse = hasDocumentForDownload
    ? actionsBase
    : actionsBase.filter((action) => action.label !== "Download");

  return (
    <div className="playground-container">
      {/* Top Navigation Bar */}
      <div className="playground-top-nav">
        <div className="playground-nav-left">
          {backLink && (
            <button className="btn-back-link" onClick={() => navigate(backLink)}>
              <ArrowLeft size={16} />
              {backLinkText}
            </button>
          )}
        </div>
        <div className="playground-nav-right">
          {actionsToUse.map((action, index) => {
            const Icon = action.icon;
            if (action.hasDropdown) {
              return (
                <div key={index} className="dropdown-wrapper" ref={dropdownRef}>
                  <button
                    type="button"
                    className={`btn-top-action ${exceptionsDropdownOpen ? "active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setExceptionsDropdownOpen(!exceptionsDropdownOpen);
                    }}
                  >
                    {exceptionStatus ? (
                      <span style={{ textTransform: 'capitalize' }}>{exceptionStatus}</span>
                    ) : (
                      action.label
                    )}
                    {Icon && <Icon size={16} />}
                  </button>
                  {exceptionsDropdownOpen && action.dropdownOptions && (
                    <div className="dropdown-menu">
                      {action.dropdownOptions.map((option, optIndex) => {
                        const OptionIcon = option.icon;
                        const isSelected = exceptionStatus === option.value;
                        return (
                          <button
                            key={optIndex}
                            className="dropdown-item"
                            onClick={() => {
                              if (option.value && handleExceptionStatusChange) {
                                handleExceptionStatusChange(option.value);
                              }
                              handleTopAction({ ...action, value: option.value });
                              setExceptionsDropdownOpen(false);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              color: isSelected ? '#7c3aed' : '#374151',
                              backgroundColor: isSelected ? '#f3f4f6' : 'transparent'
                            }}
                          >
                            {isSelected && <Check size={16} style={{ color: '#7c3aed' }} />}
                            {OptionIcon && !isSelected && <OptionIcon size={16} />}
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <button
                key={index}
                className={`btn-top-action ${action.isLoading ? "loading" : ""}`}
                onClick={() => handleTopAction(action)}
                disabled={action.disabled}
              >
                {Icon && <Icon size={16} className={action.isLoading ? "spin" : ""} />}
                {action.label}
                {action.statusText ? (
                  <span className="btn-top-action-status">{action.statusText}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="playground-main-content">
        {!hideDocumentViewer && (
          <div className={`playground-left-panel ${isDocumentPanelCollapsed ? 'collapsed' : ''}`}>
            <DocumentViewer
              documentUrl={documentUrl}
              documentType={documentType}
              documentName={documentName}
              onRegenerate={handleRegenerate}
              isCollapsed={isDocumentPanelCollapsed}
              onToggleCollapse={() => setIsDocumentPanelCollapsed(!isDocumentPanelCollapsed)}
            />
          </div>
        )}

        {/* Right Side - Dynamic Content */}
        <div className={`playground-right-panel ${hideDocumentViewer || isDocumentPanelCollapsed ? 'expanded' : ''}`}>
          {loading ? (
            <div className="playground-loading">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p>Loading content...</p>
            </div>
          ) : error ? (
            <div className="playground-error">
              <p>Error loading content: {error}</p>
              <button className="btn btn-primary" onClick={fetchContentData}>
                Retry
              </button>
            </div>
          ) : (
            <div className="dynamic-content-wrapper">
              <DynamicContentRenderer
                contentData={contentData}
                onFieldChange={handleFieldChange}
                onFieldCommit={handleFieldCommit}
                saving={saving}
                savingFieldId={savingFieldId}
                onTableChange={handleTableDataChange}
                onTableCommit={handleTableCommit}
                onAction={handleAction}
              />
            </div>
          )}
        </div>
      </div>

      {/* Audit Log Modal */}
      {auditLogOpen && (
        <AuditLogModal
          onClose={() => setAuditLogOpen(false)}
          tableName={resourceType}
          recordId={initialContent?.id}
        />
      )}
    </div>
  );
};

export default Playground;

